import { memo, useCallback, useState } from "react";
import { Loader2, Download } from "lucide-react";

import { downloadUrl } from "@/lib/utils";
import { formatBytes } from "@/lib/format";
import { useOptionalVmClient } from "@/api/context";
import type { DownloadTimelineItem } from "@/hooks/useChat";
import { MessageFrame } from "./MessageFrame";

export const DownloadCard = memo(function DownloadCard({
  item,
}: {
  item: DownloadTimelineItem;
}) {
  const vmClient = useOptionalVmClient();
  const [busy, setBusy] = useState(false);
  const title = item.label?.trim() ? item.label : item.filename;
  const showFilename = title !== item.filename;

  const url = vmClient?.getDownloadUrl(item.downloadId);
  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      if (!url) return;
      if (busy) return;
      setBusy(true);
      try {
        await downloadUrl(url, item.filename);
      } catch {
        window.open(url, "_blank", "noopener,noreferrer");
      } finally {
        setBusy(false);
      }
    },
    [url, item.filename, busy],
  );

  if (!url) return null;

  return (
    <MessageFrame side="bot">
      <a
        href={url}
        download={item.filename}
        onClick={handleClick}
        className="flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.03] px-4 py-3 transition-colors hover:bg-white/[0.06]"
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
          {busy ? (
            <Loader2 className="h-4.5 w-4.5 animate-spin text-blue-400" />
          ) : (
            <Download className="h-4.5 w-4.5 text-blue-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-medium text-zinc-200">
            {title}
          </div>
          {showFilename && (
            <div className="truncate text-[12px] text-zinc-500">
              {item.filename}
            </div>
          )}
          <div className="text-[12px] text-zinc-500">
            {formatBytes(item.size)}
          </div>
        </div>
      </a>
    </MessageFrame>
  );
});
